import { computed, effect, Injectable, signal, Signal } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { MdrService } from 'src/app/services/mdr.service';
import { MdrOpenRequest, MdrResponse } from 'src/app/model/mdr.model';
import { LruPageCache, MdrPaginatedListStore } from './paginated-list.store';
import { MdrSharedFilters, PAGE_CACHE_MAX_PAGES } from './list.types';

// --- Public types ----------------------------------------------------------

export type MdrManageFilters = MdrSharedFilters;

// --- Constants -------------------------------------------------------------

/** Buffer target in multiples of `pageSize`. Refill stops once reached. */
const BUFFER_TARGET_FACTOR = 2;

/** Refill watermark in multiples of `pageSize`. Gap with target gives hysteresis. */
const BUFFER_LOW_WATERMARK_FACTOR = 1;

// --- Store -----------------------------------------------------------------

/**
 * Store for the "Requests to Manage" list in the MDR dashboard.
 *
 * Strategy: **FIFO buffer contiguous to currentPage** + hysteresis refill +
 * optimistic local consume on Accept/Reject + LRU page cache for backward
 * navigation. Forward queue promotion is preferred over cache lookup and
 * over foreground HTTP. After Accept/Reject the server's page numbers shift,
 * so the cache is pruned for pages `> currentPage` on every consume.
 */
@Injectable()
export class MdrManageListStore extends MdrPaginatedListStore<MdrOpenRequest, MdrManageFilters> {
  /** IDs of rows with an Accept/Reject action in flight (disables per-row buttons). */
  readonly pendingActions = signal<ReadonlySet<string>>(new Set());

  protected keyOf(item: MdrOpenRequest): string {
    return item.mdr_request_id;
  }

  // --- Strategy state ------------------------------------------------------

  /**
   * FIFO of pre-fetched rows. INVARIANT: server positions strictly after the
   * last row in `displayedItems` (forward continuation of `currentPage`).
   * Navigations that change `currentPage` and `consumeLocally` MUST cancel
   * any in-flight prefetch before mutating state, otherwise a late response
   * could refill the buffer with rows that no longer belong here.
   */
  private readonly bufferItems = signal<MdrOpenRequest[]>([]);

  /** LRU page cache for backward navigation. Pruned for pages `> currentPage` on every consume. */
  private readonly pageCache = new LruPageCache<MdrOpenRequest[]>(PAGE_CACHE_MAX_PAGES);

  /** Hysteresis latch: true while we're actively refilling toward target. */
  private shouldFill = false;

  private readonly bufferTarget: Signal<number> = computed(
    () => BUFFER_TARGET_FACTOR * this.pageSize(),
  );

  private readonly bufferLowWatermark: Signal<number> = computed(
    () => BUFFER_LOW_WATERMARK_FACTOR * this.pageSize(),
  );

  constructor(private mdrService: MdrService) {
    super();

    // Hysteresis-driven refill loop. Reads every signal eagerly up-front so
    // Angular's dependency graph stays identical even when the helpers
    // short-circuit. The latch prevents one HTTP request per consumed row.
    effect(() => {
      const displayedLen = this.displayedItems().length;
      const bufLen = this.bufferItems().length;
      const isLoading = this.loading();
      const isPrefetching = this.prefetching();
      const totalItems = this.totalItems();
      const pageSize = this.pageSize();
      const currentPage = this.currentPage();
      const target = this.bufferTarget();
      const watermark = this.bufferLowWatermark();

      this.updateShouldFillLatch(bufLen, target, watermark);

      if (this.shouldPrefetchNow(displayedLen, bufLen, isLoading, isPrefetching, currentPage, pageSize, totalItems)) {
        this.prefetchNext();
      }
    });
  }

  // --- Public API ----------------------------------------------------------

  isRowPending(id: string): boolean {
    return this.pendingActions().has(id);
  }

  /** Three-tier navigation: queue promotion → cache hit → HTTP foreground load. */
  override goToPage(target: number): void {
    if (target < 1 || target === this.currentPage()) return;

    if (this.canPromoteBufferTo(target)) {
      this.cacheCurrentPage();
      this.promoteBufferToDisplayed(target);
      return;
    }

    const cached = this.pageCache.get(target);
    if (cached) {
      this.transitionToPage(target, cached);
      return;
    }

    this.cacheCurrentPage();
    this.clearBuffer();
    this.shouldFill = false;
    this.loadPage(target);
  }

  /** Reloads the current page, discarding the queue. */
  refresh(): void {
    this.clearBuffer();
    this.shouldFill = false;
    this.loadPage(this.currentPage());
  }

  /** On success: row removed locally, queue head shifted into the displayed list. */
  acceptItem(id: string): Observable<void> {
    return this.runRowAction(id, this.mdrService.acceptRequest(id));
  }

  /** On success: row removed locally, queue head shifted into the displayed list. */
  rejectItem(id: string): Observable<void> {
    return this.runRowAction(id, this.mdrService.rejectRequest(id));
  }

  // --- Base hooks ----------------------------------------------------------

  protected fetchPage(page: number): Observable<MdrResponse> {
    const { ships, passenger_name, date_from, date_to, cabin } = this.filters();
    const { by, order } = this.sort();
    return this.mdrService.getMdrRequests(
      page,
      this.pageSize(),
      ships,
      passenger_name,
      date_from,
      date_to,
      cabin,
      by,
      order,
    );
  }

  protected override onClearVolatileState(): void {
    this.clearBuffer();
    this.pageCache.clear();
    this.pendingActions.set(new Set());
    this.shouldFill = false;
  }

  /**
   * Eager prefetch of page 2 in parallel to page 1, so the first "next" click
   * doesn't fall through to a foreground HTTP load (the hysteresis effect can
   * only react *after* page 1's response lands).
   */
  protected override onWarmStart(): void {
    this.runPrefetch(2, items => {
      const fresh = this.dedupAgainstHeld(items);
      this.bufferItems.update(buf => buf.concat(fresh));
    });
  }

  // --- Private: refill effect helpers --------------------------------------

  private updateShouldFillLatch(bufLen: number, target: number, watermark: number): void {
    if (bufLen >= target) {
      this.shouldFill = false;
    } else if (bufLen < watermark) {
      this.shouldFill = true;
    }
  }

  private shouldPrefetchNow(
    displayedLen: number,
    bufLen: number,
    isLoading: boolean,
    isPrefetching: boolean,
    currentPage: number,
    pageSize: number,
    totalItems: number,
  ): boolean {
    const positionsCovered = (currentPage - 1) * pageSize + displayedLen + bufLen;
    const hasMoreItemsToFetch = positionsCovered < totalItems;
    return this.shouldFill && !isLoading && !isPrefetching && hasMoreItemsToFetch;
  }

  // --- Private: row actions ------------------------------------------------

  /**
   * Marks the id as pending for the entire duration; `finalize` guarantees
   * cleanup even if the caller unsubscribes before the response arrives.
   */
  private runRowAction(id: string, action$: Observable<void>): Observable<void> {
    if (this.pendingActions().has(id)) {
      return throwError(() => new Error('Row action already in progress'));
    }
    this.addPending(id);

    return action$.pipe(
      takeUntil(this.destroy$),
      tap(() => this.consumeLocally(id)),
      finalize(() => this.removePending(id)),
    );
  }

  private addPending(id: string): void {
    this.pendingActions.update(set => {
      const next = new Set(set);
      next.add(id);
      return next;
    });
  }

  private removePending(id: string): void {
    this.pendingActions.update(set => {
      if (!set.has(id)) return set;
      const next = new Set(set);
      next.delete(id);
      return next;
    });
  }

  // --- Private: local consume ----------------------------------------------

  /**
   * Optimistic Accept/Reject side-effect on local state.
   * Order: trim → cancel stale prefetch → backfill → prune cache → empty-page check.
   */
  private consumeLocally(consumedId: string): void {
    const trimmed = this.removeRowFromDisplayed(consumedId);
    if (trimmed === null) return;

    // A prefetch in flight was issued BEFORE the consume: its GET may have
    // hit the server before the Accept/Reject POST and returned the pre-shift
    // view. Landing it would contaminate the buffer with rows that no longer
    // correspond to the post-consume forward chain.
    this.cancelPrefetchOnly();

    this.applyConsumeToState(trimmed);
    this.invalidateCachePagesAfterCurrent();
    this.handleEmptyLastPage();
  }

  private applyConsumeToState(trimmedDisplayed: MdrOpenRequest[]): void {
    const { displayed, buffer } = this.fillDisplayedFromBuffer(trimmedDisplayed, this.bufferItems(), 1);
    this.displayedItems.set(displayed);
    this.bufferItems.set(buffer);
    this.totalItems.update(t => Math.max(0, t - 1));
  }

  /**
   * Items at positions `> P` shifted left by one server-side. Cached pages
   * after `currentPage` now reference stale rows — drop them. Earlier pages
   * are untouched.
   */
  private invalidateCachePagesAfterCurrent(): void {
    const current = this.currentPage();
    this.pageCache.prune(page => page > current);
  }

  /** Step back one page if we just emptied the last one (no queue to backfill). */
  private handleEmptyLastPage(): void {
    if (this.displayedItems().length === 0 && this.currentPage() > 1) {
      this.clearBuffer();
      this.shouldFill = false;
      this.loadPage(this.currentPage() - 1);
    }
  }

  /** Returns the displayed list without the given row, or `null` if not found. */
  private removeRowFromDisplayed(id: string): MdrOpenRequest[] | null {
    const list = this.displayedItems();
    const idx = list.findIndex(r => r.mdr_request_id === id);
    if (idx === -1) return null;
    return list.slice(0, idx).concat(list.slice(idx + 1));
  }

  /**
   * Shifts up to `maxSlots` rows from `buffer` into `displayed`, skipping any
   * row whose id is already in `displayed` (dedup against shifted server pages).
   * Pure: callers decide whether to commit the result. Used both by the
   * Accept/Reject backfill path (maxSlots=1) and the post-prefetch top-up
   * (maxSlots = pageSize - displayed.length).
   */
  private fillDisplayedFromBuffer(
    displayed: MdrOpenRequest[],
    buffer: MdrOpenRequest[],
    maxSlots: number,
  ): { displayed: MdrOpenRequest[]; buffer: MdrOpenRequest[] } {
    if (maxSlots <= 0 || buffer.length === 0) {
      return { displayed, buffer };
    }

    const displayedIds = new Set(displayed.map(r => r.mdr_request_id));
    const additions: MdrOpenRequest[] = [];
    const remainingBuffer: MdrOpenRequest[] = [];
    for (const item of buffer) {
      if (additions.length < maxSlots && !displayedIds.has(item.mdr_request_id)) {
        additions.push(item);
      } else {
        remainingBuffer.push(item);
      }
    }

    if (additions.length === 0) {
      return { displayed, buffer };
    }
    return {
      displayed: displayed.concat(additions),
      buffer: remainingBuffer,
    };
  }

  // --- Private: prefetch ---------------------------------------------------

  /**
   * Fetches the page containing the first item we don't have yet, derived as
   * `floor(itemsInHand / pageSize) + 1`. Adapts to the server's shifting
   * pagination as items are accepted (a monotonic counter would drift past
   * `totalPages` and stop refilling). `topUp` recovers displayed count if it
   * dipped below `pageSize`.
   */
  private prefetchNext(): void {
    if (this.prefetching()) return;
    if (this.bufferItems().length >= this.bufferTarget()) return;

    const pageSize = this.pageSize();
    const itemsInHand = this.displayedItems().length + this.bufferItems().length;
    const positionsCovered = (this.currentPage() - 1) * pageSize + itemsInHand;
    if (positionsCovered >= this.totalItems()) return;

    const targetPage = Math.floor(positionsCovered / pageSize) + 1;

    this.runPrefetch(targetPage, items => {
      const fresh = this.dedupAgainstHeld(items);
      this.bufferItems.update(buf => buf.concat(fresh));
      this.topUpDisplayedFromBuffer();
    });
  }

  /** Filters `incoming` against IDs already held in `displayed ∪ buffer`. */
  private dedupAgainstHeld(incoming: MdrOpenRequest[]): MdrOpenRequest[] {
    const existingIds = new Set<string>([
      ...this.displayedItems().map(r => r.mdr_request_id),
      ...this.bufferItems().map(r => r.mdr_request_id),
    ]);
    return incoming.filter(r => !existingIds.has(r.mdr_request_id));
  }

  /**
   * After a late prefetch lands, `displayed` may be well below `pageSize`
   * (user kept consuming with an empty queue). Without this, the table would
   * stay semi-empty for the session — the consume backfill only adds 1/accept.
   */
  private topUpDisplayedFromBuffer(): void {
    const displayed = this.displayedItems();
    const buffer = this.bufferItems();
    const slotsToFill = this.pageSize() - displayed.length;
    const next = this.fillDisplayedFromBuffer(displayed, buffer, slotsToFill);
    if (next.displayed === displayed) return;
    this.displayedItems.set(next.displayed);
    this.bufferItems.set(next.buffer);
  }

  // --- Private: navigation helpers -----------------------------------------

  /** True when the queue holds enough rows to cover every page between current and target. */
  private canPromoteBufferTo(target: number): boolean {
    const current = this.currentPage();
    if (target <= current) return false;
    const itemsNeeded = (target - current) * this.pageSize();
    return this.bufferItems().length >= itemsNeeded;
  }

  /**
   * Slides the displayed window forward by `(target - currentPage)` pages.
   * Skipped intermediate pages are dropped from the queue (server-side they
   * stay PENDING and will be refetched, with dedup, on backward navigation).
   */
  private promoteBufferToDisplayed(target: number): void {
    const pageSize = this.pageSize();
    const skippedCount = (target - this.currentPage() - 1) * pageSize;
    const buffer = this.bufferItems();
    const newDisplayed = buffer.slice(skippedCount, skippedCount + pageSize);
    const newBuffer = buffer.slice(skippedCount + pageSize);

    this.displayedItems.set(newDisplayed);
    this.bufferItems.set(newBuffer);
    this.currentPage.set(target);
  }

  /**
   * Atomic transition into a known set of rows for `target`. Used by the
   * cache-hit branch: snapshot the outgoing page, cancel a now-stale
   * prefetch (its response was computed against the old `currentPage` and
   * would refill with rows no longer forward-contiguous to `target`), drop
   * the buffer + refill latch, then publish the new displayed/currentPage.
   *
   * Ordering matters: cacheCurrentPage must read `currentPage` BEFORE it
   * advances, and the prefetch must be cancelled BEFORE the buffer clears
   * (otherwise a late `next` callback could repopulate the cleared buffer).
   */
  private transitionToPage(target: number, newDisplayed: MdrOpenRequest[]): void {
    this.cacheCurrentPage();
    this.cancelPrefetchOnly();
    this.clearBuffer();
    this.shouldFill = false;
    this.displayedItems.set(newDisplayed);
    this.currentPage.set(target);
  }

  /** Snapshots `currentPage` into the backward cache. No-op when displayed is empty. */
  private cacheCurrentPage(): void {
    const items = this.displayedItems();
    if (items.length === 0) return;
    this.pageCache.set(this.currentPage(), items);
  }

  private clearBuffer(): void {
    this.bufferItems.set([]);
  }
}
